import React, { useState } from "react";
import { ChevronDown, Calendar } from "lucide-react";
import { TrendUp, ExportCurve, Add } from "iconsax-reactjs";

const Dashboard = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    source: "Source",
    status: "Status",
    assigned: "Assigned to",
  });

  const StatCard = ({ title, value, change }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[#94979C] text-[14px] xl:text-[14px] font-medium">
          {title}
        </div>
        <div className="flex items-center justify-between">
          <button className="text-gray-400 hover:text-gray-300 p-1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div className="text-[#F7F7F7] text-[28px] xl:text-[20px] font-bold">
            {value}
          </div>
          <div className="flex items-center text-white text-sm xl:text-[10px] font-medium">
            <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
            {change}%
          </div>
        </div>
      </div>
    </div>
  );

  const LeadRow = ({
    company,
    owner,
    ownerEmail,
    phone,
    status,
    statusColor,
    value,
    lastContact,
    initials,
  }) => (
    <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
      <td className="py-4 px-4 text-white font-medium">{company}</td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 text-sm font-medium">
            {initials}
          </div>
          <div>
            <div className="text-white font-medium">{owner}</div>
            <div className="text-gray-400 text-sm">{ownerEmail}</div>
          </div>
        </div>
      </td>
      <td className="py-4 px-4 text-gray-400">{phone}</td>
      <td className="py-4 px-4">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium ${statusColor}`}
        >
          <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
          {status}
        </span>
      </td>
      <td className="py-4 px-4 text-white font-medium">{value}</td>
      <td className="py-4 px-4 text-gray-400">{lastContact}</td>
      <td className="py-4 px-4 text-right">
        <button className="text-gray-400 hover:text-white transition-colors p-1">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </button>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Title Section */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Leads
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage all your audio files, summaries and AI insights.
            </p>
          </div>

          {/* Buttons Section */}
          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            {/* Import Data Button */}
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0">
              <ExportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Import Data</span>
            </button>

            {/* Bulk Actions Button */}
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0">
              <span className="whitespace-nowrap">Bulk Actions</span>
            </button>

            {/* New Lead Button */}
            <button
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
      0px 36px 32px -16px #427BCA36,
      0px 8px 16px 0px #2F578F1F,
      0px 2px 4px -8px #427BCA14,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                borderImageSlice: "1",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "radial-gradient(50% 50% at 50% 50%, #5A9BE8 0%, #427BCA 100%)";
                e.currentTarget.style.boxShadow = `
        0px 42px 36px -12px #427BCA80,
        0px 12px 20px 0px #2F578F3D,
        0px 4px 8px -4px #427BCA28,
        0px 12px 28px -12px #427BCA inset,
        0px -28px 36px 0px #FFFFFF4D inset,
        0px 0px 16px 0px #FFFFFF inset
      `;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)";
                e.currentTarget.style.boxShadow = `
      0px 36px 32px -16px #427BCA36,
      0px 8px 16px 0px #2F578F1F,
      0px 2px 4px -8px #427BCA14,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `;
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Lead</span>
            </button>
          </div>
        </div>
        {/* Header with filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(selectedFilters).map(([key, value]) => (
              <button
                key={key}
                className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                {value}
                <ChevronDown size={14} />
              </button>
            ))}
          </div>

          <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors self-start lg:self-auto">
            <Calendar size={14} />
            Select dates
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title="Total Leads" value="523" change="70" />
          <StatCard title="New Leads Today" value="30" change="70" />
          <StatCard title="Conversion Rate" value="35" change="70" />
          <StatCard title="Pending Callback" value="12" change="70" />
        </div>

        {/* Table */}
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mb-8">
          {/* Table Header Section */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Leads{" "}
                <span className="text-gray-400 text-sm ml-2">240 leads</span>
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Keep track of your leads.
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors">
              <ExportCurve size={16} />
              Export
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-[#13161B]">
                <tr className="border-b border-[#22262F]">
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Lead name/Company
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Contact Owner
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Phone Number
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Status
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Value
                  </th>
                  <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                    Last Contact
                  </th>
                  <th className="text-right py-4 px-4 text-gray-400 font-medium text-sm"></th>
                </tr>
              </thead>
              <tbody>
                <LeadRow
                  company="Beta Solutions"
                  owner="Jessica Kim"
                  ownerEmail="jessica.kim@techworld.com"
                  phone="+1 202-555-01"
                  status="New"
                  statusColor="text-blue-400 bg-[#0C0E12]"
                  value="£55,000"
                  lastContact="1h 45m ago"
                  initials="JK"
                />
                <LeadRow
                  company="Gamma Innovations"
                  owner="Alex Martinez"
                  ownerEmail="alex.m@innovatech.com"
                  phone="+33 1 23 45 67"
                  status="Qualified"
                  statusColor="text-green-400 bg-[#0C0E12]"
                  value="£60,000"
                  lastContact="30m ago"
                  initials="AM"
                />
                <LeadRow
                  company="Delta Enterprises"
                  owner="Hannah Roberts"
                  ownerEmail="h.roberts@designhub.com"
                  phone="+81 3-1234-56"
                  status="Contacted"
                  statusColor="text-orange-400 bg-[#0C0E12]"
                  value="£65,000"
                  lastContact="15m ago"
                  initials="HR"
                />
                <LeadRow
                  company="Epsilon Technologies"
                  owner="Michael Green"
                  ownerEmail="michael.green@creativedesigns.org"
                  phone="+49 30 123456"
                  status="Qualified"
                  statusColor="text-green-400 bg-[#0C0E12]"
                  value="£70,000"
                  lastContact="5m ago"
                  initials="MG"
                />
                <LeadRow
                  company="Zeta Industries"
                  owner="Laura Thomas"
                  ownerEmail="laura.t@websolutions.net"
                  phone="+61 2 9876 543"
                  status="Disqualified"
                  statusColor="text-red-400 bg-[#0C0E12]"
                  value="£75,000"
                  lastContact="just now"
                  initials="LT"
                />
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
            <div className="flex gap-2">
              <button className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors">
                Previous
              </button>
              <button className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors">
                Next
              </button>
            </div>
            <span className="text-gray-400 text-sm">Page 1 of 10</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
